import { useTranslation } from "react-i18next";
import { COUNTRIES } from "../i18n/countries";
import { Select } from "./Select";

/**
 * Shared form fields for a company address-book entry (delivery or billing).
 * Reused by the admin management dialog (AdminCompanyDetailPage) and the
 * customer inline add-address dialog on the order editor — same field set,
 * same labels, same purpose checkboxes. Controlled by the parent.
 */
export function AddressFormFields(props: {
  label: string; setLabel: (v: string) => void;
  recipientName: string; setRecipientName: (v: string) => void;
  addressLine1: string; setAddressLine1: (v: string) => void;
  addressLine2: string; setAddressLine2: (v: string) => void;
  city: string; setCity: (v: string) => void;
  postalCode: string; setPostalCode: (v: string) => void;
  country: string; setCountry: (v: string) => void;
  vatNumber: string; setVatNumber: (v: string) => void;
  usableForDelivery: boolean; setUsableForDelivery: (v: boolean) => void;
  usableForBilling: boolean; setUsableForBilling: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={twoCol}>
        <FieldLabel text={t("company.addresses.label", "Label")} hint={t("common.optional", "optional")}>
          <input
            className="input"
            value={props.label}
            onChange={(e) => props.setLabel(e.target.value)}
            placeholder={t("company.addresses.labelPlaceholder", "e.g. HQ, Warehouse Novi Sad")}
          />
        </FieldLabel>
        <FieldLabel text={t("company.addresses.recipient", "Recipient name")} hint={t("common.optional", "optional")}>
          <input
            className="input"
            value={props.recipientName}
            onChange={(e) => props.setRecipientName(e.target.value)}
            placeholder={t("company.addresses.recipientPlaceholder", "e.g. ACME Kćerka d.o.o.")}
          />
        </FieldLabel>
      </div>

      <FieldLabel text={t("company.addresses.addressLine1", "Address line 1")} required>
        <input
          className="input"
          value={props.addressLine1}
          onChange={(e) => props.setAddressLine1(e.target.value)}
          required
        />
      </FieldLabel>
      <FieldLabel text={t("company.addresses.addressLine2", "Address line 2")} hint={t("common.optional", "optional")}>
        <input
          className="input"
          value={props.addressLine2}
          onChange={(e) => props.setAddressLine2(e.target.value)}
        />
      </FieldLabel>

      <div style={twoCol}>
        <FieldLabel text={t("company.addresses.postalCode", "Postal code")}>
          <input
            className="input"
            value={props.postalCode}
            onChange={(e) => props.setPostalCode(e.target.value)}
          />
        </FieldLabel>
        <FieldLabel text={t("company.addresses.city", "City")}>
          <input
            className="input"
            value={props.city}
            onChange={(e) => props.setCity(e.target.value)}
          />
        </FieldLabel>
      </div>

      <div style={twoCol}>
        <FieldLabel text={t("company.addresses.country", "Country")}>
          <Select<string>
            value={props.country}
            onChange={props.setCountry}
            ariaLabel={t("company.addresses.country", "Country")}
            options={[
              { value: "", label: t("common.optional", "optional") },
              ...COUNTRIES.map((c) => ({ value: c.code, label: c.name })),
            ]}
          />
        </FieldLabel>
        <FieldLabel text={t("company.addresses.vat", "VAT")} hint={t("company.addresses.vatHint", "for billing")}>
          <input
            className="input"
            value={props.vatNumber}
            onChange={(e) => props.setVatNumber(e.target.value)}
          />
        </FieldLabel>
      </div>

      <fieldset style={fieldset}>
        <legend style={legend}>{t("company.addresses.purpose", "Usable for")}</legend>
        <label style={checkRow}>
          <input
            type="checkbox"
            checked={props.usableForDelivery}
            onChange={(e) => props.setUsableForDelivery(e.target.checked)}
          />
          {t("company.addresses.delivery", "Delivery")}
        </label>
        <label style={checkRow}>
          <input
            type="checkbox"
            checked={props.usableForBilling}
            onChange={(e) => props.setUsableForBilling(e.target.checked)}
          />
          {t("company.addresses.billing", "Billing")}
        </label>
      </fieldset>
    </div>
  );
}

function FieldLabel({
  text, hint, required, children,
}: {
  text: string; hint?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={labelText}>
        {text}
        {required && <span style={{ color: "var(--err)" }}> *</span>}
        {hint && !required && <span style={{ color: "var(--ink-4)", textTransform: "none", fontWeight: 400 }}> ({hint})</span>}
      </span>
      {children}
    </label>
  );
}

const twoCol: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const labelText: React.CSSProperties = {
  fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600,
};
const fieldset: React.CSSProperties = {
  display: "flex", gap: 18, alignItems: "center", border: "1px solid var(--border)",
  borderRadius: 8, padding: "8px 12px", margin: 0,
};
const legend: React.CSSProperties = {
  fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, padding: "0 4px",
};
const checkRow: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-2)", cursor: "pointer",
};
